import { useEffect, useState } from "react";
import { MousePointerClick, Loader2 } from "lucide-react";
import { getWorkshopStats, liveGet } from "@/lib/api";
import { LeadsList } from "./LeadsList";
import { useLive, LiveBadge } from "./useLive";

const convColor = (c) => (c == null ? "#525252" : c >= 10 ? "#16A34A" : c >= 5 ? "#D97706" : "#D92D20");
const fmtDay = (g) => {
  const d = new Date(`${g}T12:00:00`);
  return d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" });
};

export function LandingPanel() {
  const live = useLive();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!live) return;
    setLoading(true);
    (live.live ? liveGet("workshop/stats", { days: 14 }) : getWorkshopStats(14)).then(setStats).catch(() => setStats(null)).finally(() => setLoading(false));
  }, [live]);

  const tot = stats?.totali || { views: 0, form_clicks: 0, leads: 0, conv: null };
  const giorni = stats?.giorni || [];

  return (
    <section className="border border-[#262626] rounded-xl bg-[#111111] p-6 space-y-5" data-testid="strategy-landing">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-white text-lg flex items-center gap-2">
            <MousePointerClick size={18} style={{ color: "#D97706" }} /> Landing workshop — visite, modulo, iscritti
          </h2>
          <p className="text-xs text-neutral-500 mt-1">Ultimi 14 giorni, contati dall'app (una visita per sessione). Verde ≥ 10% · giallo 5-10% · rosso &lt; 5%. Gli iscritti dal modulo Meta non passano di qui.</p>
        </div>
        <LiveBadge live={live} testid="landing-live-badge" />
      </div>

      {loading || !live ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 size={14} className="animate-spin" /> Carico…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="strategy-landing-totali">
            {[
              ["Visite", tot.views, "landing-tot-views"],
              ["Hanno toccato il modulo", tot.form_clicks, "landing-tot-form"],
              ["Iscritti", tot.leads, "landing-tot-leads"],
              ["Conversione", tot.conv == null ? "–" : `${tot.conv}%`, "landing-tot-conv"],
            ].map(([l, v, tid], i) => (
              <div key={l} className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-3" data-testid={tid}>
                <p className="text-[0.65rem] uppercase tracking-wide text-neutral-500">{l}</p>
                <p className="font-display font-bold text-2xl mt-1" style={{ color: i === 3 ? convColor(tot.conv) : "#fff" }}>{v}</p>
              </div>
            ))}
          </div>

          {giorni.length === 0 ? (
            <p className="text-sm text-neutral-500" data-testid="strategy-landing-empty">Ancora nessuna visita registrata: i contatori partono dal prossimo deploy della landing.</p>
          ) : (
            <table className="w-full text-sm" data-testid="strategy-landing-table">
              <thead>
                <tr className="text-[0.65rem] uppercase tracking-wide text-neutral-500 border-b border-[#262626]">
                  <th className="text-left py-2 font-medium">Giorno</th>
                  <th className="text-right py-2 font-medium">Visite</th>
                  <th className="text-right py-2 font-medium">Modulo</th>
                  <th className="text-right py-2 font-medium">Iscritti</th>
                  <th className="text-right py-2 font-medium">Conv.</th>
                </tr>
              </thead>
              <tbody>
                {giorni.map((g) => (
                  <tr key={g.giorno} className="border-b border-[#1a1a1a]" data-testid={`landing-day-${g.giorno}`}>
                    <td className="py-2 text-neutral-300 capitalize">{fmtDay(g.giorno)}</td>
                    <td className="py-2 text-right text-neutral-300">{g.views}</td>
                    <td className="py-2 text-right text-neutral-400">{g.form_clicks}</td>
                    <td className="py-2 text-right text-white font-semibold">{g.leads}</td>
                    <td className="py-2 text-right font-semibold" style={{ color: convColor(g.conv) }}>{g.conv == null ? "–" : `${g.conv}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <LeadsList live={live} />
        </>
      )}
    </section>
  );
}
